import { Container, Sprite, Text, TextStyle, Graphics, Texture } from 'pixi.js';
import { defineQuery } from 'bitecs';
import { Health, Inventory, Movement, Wetness, Time, Hunger, Thirst } from '../world.js';
import { MONTHS, TICKS_PER_MINUTE, TICKS_PER_HOUR } from './time.js';

const UI_WIDTH = 250;
const UI_PADDING = 15;
const LINE_HEIGHT = 25;
const BAR_HEIGHT = 15;
const TEXT_SPACING = 10;
const CORNER_RADIUS = 20;
const SEASON_COLORS = {
    0: 0x00FF00, // Spring - Green
    1: 0xFFFF00, // Summer - Yellow
    2: 0xFFA500, // Autumn - Orange
    3: 0xFFFFFF  // Winter - White
};

// Create a gradient background
function createGradientBackground(width, height) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    
    const gradient = ctx.createLinearGradient(0, 0, width, 0);
    gradient.addColorStop(0, 'rgba(0, 0, 0, 0.8)');
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0.6)');
    
    // Draw with rounded corners
    ctx.beginPath();
    ctx.moveTo(CORNER_RADIUS, 0);
    ctx.lineTo(width - CORNER_RADIUS, 0);
    ctx.quadraticCurveTo(width, 0, width, CORNER_RADIUS);
    ctx.lineTo(width, height - CORNER_RADIUS);
    ctx.quadraticCurveTo(width, height, width - CORNER_RADIUS, height);
    ctx.lineTo(CORNER_RADIUS, height);
    ctx.quadraticCurveTo(0, height, 0, height - CORNER_RADIUS);
    ctx.lineTo(0, CORNER_RADIUS);
    ctx.quadraticCurveTo(0, 0, CORNER_RADIUS, 0);
    ctx.closePath();
    
    ctx.fillStyle = gradient;
    ctx.fill();
    
    return Texture.from(canvas);
}

export function createUISystem(app) {
    // Check if UI container already exists
    const existingContainer = app.gameplayContainer.children.find(child => 
        child.constructor.name === 'Container' && 
        child.children.some(c => c.constructor.name === 'Sprite' && c.texture && c.texture.baseTexture.resource)
    );

    if (existingContainer) {
        console.log('Reusing existing UI container');
        return function updateUI(world) {
            // Update existing UI
            return world;
        };
    }

    // Create a separate UI container that's fixed to the screen
    const container = new Container();
    container.sortableChildren = true;
    container.zIndex = 1000; // Ensure UI is on top
    
    // Add to gameplay container
    if (app.gameplayContainer) {
        app.gameplayContainer.addChild(container);
    }
    console.log('UI container added to gameplay container:', container);
    
    // Function to update UI position
    const updateUIPosition = () => {
        // Position relative to screen
        container.position.x = window.innerWidth - UI_WIDTH - UI_PADDING;
        container.position.y = UI_PADDING;
        console.log('UI container position updated:', container.position);
        
        // Update background size and position
        if (background) {
            background.width = UI_WIDTH;
            background.height = window.innerHeight - (UI_PADDING * 2);
            background.position.set(0, 0);
            console.log('Background size updated:', background.width, background.height);
        }
    };
    
    // Create background with gradient
    const background = new Sprite(createGradientBackground(UI_WIDTH, window.innerHeight));
    container.addChild(background);
    console.log('Background added to container');
    
    // Initial position update
    updateUIPosition();
    
    // Listen for resize events
    window.addEventListener('resize', () => {
        updateUIPosition();
    });
    
    // Create text styles
    const titleStyle = new TextStyle({
        fontFamily: '"Press Start 2P"',
        fontSize: 16,
        fill: 0xFFFFFF,
        dropShadow: true,
        dropShadowColor: 0x000000,
        dropShadowBlur: 4,
        dropShadowDistance: 2,
        letterSpacing: 2
    });
    
    const attributeStyle = new TextStyle({
        fontFamily: '"Press Start 2P"',
        fontSize: 14,
        fill: 0xFFFFFF,
        dropShadow: true,
        dropShadowColor: 0x000000,
        dropShadowBlur: 2,
        dropShadowDistance: 1,
        letterSpacing: 1
    });
    
    const propertyStyle = new TextStyle({
        fontFamily: '"Press Start 2P"',
        fontSize: 12,
        fill: 0xCCCCCC,
        dropShadow: true,
        dropShadowColor: 0x000000,
        dropShadowBlur: 2,
        dropShadowDistance: 1,
        letterSpacing: 1
    });
    
    // Create title texts with section dividers
    const permanentTitle = new Text('Attributes', titleStyle);
    permanentTitle.x = UI_PADDING;
    permanentTitle.y = UI_PADDING;
    permanentTitle.visible = true;
    container.addChild(permanentTitle);
    // console.log('Attributes title added:', permanentTitle);
    
    // Add divider line
    const divider1 = new Graphics();
    divider1.setStrokeStyle({ width: 1, color: 0xFFFFFF, alpha: 0.3 });
    divider1.moveTo(0, 0);
    divider1.lineTo(UI_WIDTH, 0);
    divider1.visible = true;
    container.addChild(divider1);
    // console.log('Attributes divider added:', divider1);
    
    const temporaryTitle = new Text('Status', titleStyle);
    temporaryTitle.x = UI_PADDING;
    temporaryTitle.y = window.innerHeight / 2;
    temporaryTitle.visible = true;
    container.addChild(temporaryTitle);
    // console.log('Status title added:', temporaryTitle);
    
    // Add divider line
    const divider2 = new Graphics();
    divider2.setStrokeStyle({ width: 1, color: 0xFFFFFF, alpha: 0.3 });
    divider2.moveTo(0, 0);
    divider2.lineTo(UI_WIDTH, 0);
    divider2.visible = true;
    container.addChild(divider2);
    // console.log('Status divider added:', divider2);
    
    // Create text objects for attributes and properties
    const healthText = new Text('', attributeStyle);
    const woodText = new Text('', attributeStyle);
    const foodText = new Text('', attributeStyle);
    const toolsText = new Text('', attributeStyle);
    
    const movementText = new Text('', propertyStyle);
    const swimmingText = new Text('', propertyStyle);
    
    // Create status bars with improved styling
    function createStatusBar(label, y, color) {
        const container = new Container();
        container.x = UI_PADDING;
        container.y = y;
        
        // Create label (static, never changes)
        const labelText = new Text(label + ':', propertyStyle);
        labelText.x = 0;
        labelText.y = (BAR_HEIGHT - labelText.height) / 2;
        container.addChild(labelText);
        
        // Create bar container
        const barContainer = new Container();
        barContainer.x = labelText.x + labelText.width + TEXT_SPACING;
        barContainer.y = 0;
        container.addChild(barContainer);
        
        // Create bar background with rounded corners (only once)
        const barBackground = new Graphics();
        barBackground.fill({ color: 0x333333 });
        barBackground.roundRect(0, 0, UI_WIDTH - labelText.x - labelText.width - TEXT_SPACING - UI_PADDING * 2, BAR_HEIGHT, CORNER_RADIUS);
        barContainer.addChild(barBackground);
        
        // Create bar fill with rounded corners (only once)
        const barFill = new Graphics();
        barFill.fill({ color: color });
        barFill.roundRect(0, 0, 0, BAR_HEIGHT, CORNER_RADIUS);
        barContainer.addChild(barFill);
        
        // Create percentage text
        const percentageText = new Text(
            '0%',
            new TextStyle({
                fontFamily: '"Press Start 2P"',
                fontSize: 10,
                fill: 0xFFFFFF,
                dropShadow: true,
                dropShadowColor: 0x000000,
                dropShadowBlur: 2,
                dropShadowDistance: 1,
                letterSpacing: 1
            })
        );
        percentageText.x = barBackground.width / 2 - percentageText.width / 2;
        percentageText.y = (BAR_HEIGHT - percentageText.height) / 2;
        barContainer.addChild(percentageText);
        
        return { container, barFill, percentageText, barBackground, maxWidth: barBackground.width };
    }
    
    // Create status bars
    const wetnessBar = createStatusBar('Wetness', window.innerHeight / 2 + LINE_HEIGHT * 3, 0x1E90FF);
    const hungerBar = createStatusBar('Hunger', window.innerHeight / 2 + LINE_HEIGHT * 4, 0xFFA500);
    const thirstBar = createStatusBar('Thirst', window.innerHeight / 2 + LINE_HEIGHT * 5, 0x00BFFF);
    
    container.addChild(wetnessBar.container);
    container.addChild(hungerBar.container);
    container.addChild(thirstBar.container);
    
    // Create time container with improved styling
    const timeContainer = new Container();
    timeContainer.x = UI_PADDING;
    timeContainer.y = window.innerHeight / 2 + LINE_HEIGHT * 6;
    container.addChild(timeContainer);
    
    // Create date text
    const dateText = new Text('', propertyStyle);
    dateText.x = 0;
    dateText.y = 0;
    timeContainer.addChild(dateText);
    
    // Create time text
    const timeText = new Text('', propertyStyle);
    timeText.x = 0;
    timeText.y = LINE_HEIGHT;
    timeContainer.addChild(timeText);
    
    // Create season indicator with improved styling
    const seasonIndicator = new Graphics();
    seasonIndicator.x = dateText.x + TEXT_SPACING;
    seasonIndicator.y = 0;
    timeContainer.addChild(seasonIndicator);
    
    // Create day/night indicator with improved styling
    const dayNightIndicator = new Graphics();
    dayNightIndicator.x = timeText.x + TEXT_SPACING;
    dayNightIndicator.y = LINE_HEIGHT;
    timeContainer.addChild(dayNightIndicator);
    
    // Create drink prompt text
    const drinkPrompt = new Text('Press E to drink', new TextStyle({
        fontFamily: 'Arial',
        fontSize: 12,
        fill: 0x00FF00
    }));
    drinkPrompt.x = UI_PADDING;
    drinkPrompt.y = window.innerHeight / 2 + LINE_HEIGHT * 7;
    drinkPrompt.visible = false;
    container.addChild(drinkPrompt);
    
    // Position attribute texts
    healthText.x = UI_PADDING;
    healthText.y = UI_PADDING + LINE_HEIGHT;
    woodText.x = UI_PADDING;
    woodText.y = UI_PADDING + LINE_HEIGHT * 2;
    foodText.x = UI_PADDING;
    foodText.y = UI_PADDING + LINE_HEIGHT * 3;
    toolsText.x = UI_PADDING;
    toolsText.y = UI_PADDING + LINE_HEIGHT * 4;
    
    // Position property texts
    movementText.x = UI_PADDING;
    movementText.y = window.innerHeight / 2 + LINE_HEIGHT;
    swimmingText.x = UI_PADDING;
    swimmingText.y = window.innerHeight / 2 + LINE_HEIGHT * 2;
    
    // Add texts to container
    container.addChild(healthText, woodText, foodText, toolsText);
    container.addChild(movementText, swimmingText);
    
    const playerQuery = defineQuery([Health, Inventory, Movement, Wetness, Hunger, Thirst]);
    const timeQuery = defineQuery([Time]);
    
    // Function to format time
    function formatTime(ticks) {
        const hour = Math.floor(ticks / TICKS_PER_HOUR);
        const minute = Math.floor((ticks % TICKS_PER_HOUR) / TICKS_PER_MINUTE);
        return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    }

    // Function to format date
    function formatDate(day, month, year) {
        return `${MONTHS[month - 1]} ${day}, Year ${year}`;
    }

    // Cache for text values to avoid unnecessary updates
    const textCache = {
        health: -1,
        wood: -1,
        food: -1,
        tools: -1,
        movement: false,
        swimming: false,
        wetness: -1,
        hunger: -1,
        thirst: -1,
        time: '',
        date: '',
        isDay: true,
        season: -1,
        canDrink: false
    };

    return function updateUI(world) {
        const players = playerQuery(world);
        if (players.length === 0) return;
        
        const player = players[0];
        
        // Update attribute texts only if values changed
        const health = Health.value[player];
        if (health !== textCache.health) {
            healthText.text = `Health: ${health}`;
            textCache.health = health;
        }

        const wood = Inventory.wood[player];
        if (wood !== textCache.wood) {
            woodText.text = `Wood: ${wood}`;
            textCache.wood = wood;
        }

        const food = Inventory.food[player];
        if (food !== textCache.food) {
            foodText.text = `Food: ${food}`;
            textCache.food = food;
        }

        const tools = Inventory.tools[player];
        if (tools !== textCache.tools) {
            toolsText.text = `Tools: ${tools}`;
            textCache.tools = tools;
        }
        
        // Update property texts only if values changed
        const isWalking = Movement.isWalking[player] === 1;
        if (isWalking !== textCache.movement) {
            movementText.text = `Movement: ${isWalking ? 'Walking' : 'Idle'}`;
            textCache.movement = isWalking;
        }

        const isSwimming = Movement.isSwimming[player] === 1;
        if (isSwimming !== textCache.swimming) {
            swimmingText.text = `Swimming: ${isSwimming ? 'Yes' : 'No'}`;
            textCache.swimming = isSwimming;
        }
        
        // Update status bars only if values changed
        const wetness = Math.round(Wetness.value[player]);
        if (wetness !== textCache.wetness) {
            const wetnessBarWidth = wetnessBar.maxWidth * (wetness / 100);
            wetnessBar.barFill.width = wetnessBarWidth;
            wetnessBar.percentageText.text = `${wetness}%`;
            textCache.wetness = wetness;
        }

        const hunger = Math.round(Hunger.value[player]);
        if (hunger !== textCache.hunger) {
            const hungerBarWidth = hungerBar.maxWidth * (hunger / 100);
            hungerBar.barFill.width = hungerBarWidth;
            hungerBar.percentageText.text = `${hunger}%`;
            textCache.hunger = hunger;
        }

        const thirst = Math.round(Thirst.value[player]);
        if (thirst !== textCache.thirst) {
            const thirstBarWidth = thirstBar.maxWidth * (thirst / 100);
            thirstBar.barFill.width = thirstBarWidth;
            thirstBar.percentageText.text = `${thirst}%`;
            textCache.thirst = thirst;
        }
        
        // Update time display only if time system is enabled and values changed
        const time = world.timeEntity;
        if (time) {
            const currentTicks = Time.ticks[time];
            const currentDay = Time.day[time];
            const currentMonth = Time.month[time];
            const currentYear = Time.year[time];
            const isDay = Time.isDay[time] === 1;
            const currentSeason = Time.season[time];

            const newTimeText = formatTime(currentTicks);
            if (newTimeText !== textCache.time) {
                timeText.text = newTimeText;
                textCache.time = newTimeText;
            }

            const newDateText = formatDate(currentDay, currentMonth, currentYear);
            if (newDateText !== textCache.date) {
                dateText.text = newDateText;
                textCache.date = newDateText;
            }

            // Update indicators only if day/night state changed
            if (isDay !== textCache.isDay) {
                dayNightIndicator.clear();
                dayNightIndicator.fill({ color: isDay ? 0xFFFF00 : 0x0000FF });
                dayNightIndicator.circle(0, 0, 5);
                textCache.isDay = isDay;
            }

            // Update season indicator only if season changed
            if (currentSeason !== textCache.season) {
                seasonIndicator.clear();
                const seasonSize = 12;
                seasonIndicator.fill({ color: SEASON_COLORS[currentSeason] });
                seasonIndicator.circle(seasonSize / 2, seasonSize / 2, seasonSize / 2);
                textCache.season = currentSeason;
            }
        } else {
            // Hide time-related elements when time is disabled
            if (textCache.time !== 'Time Disabled') {
                timeText.text = 'Time Disabled';
                dateText.text = '';
                dayNightIndicator.clear();
                seasonIndicator.clear();
                textCache.time = 'Time Disabled';
                textCache.date = '';
            }
        }
        
        // Update drink prompt only if state changed
        const canDrink = Thirst.canDrink[player] === 1;
        if (canDrink !== textCache.canDrink) {
            drinkPrompt.visible = canDrink;
            textCache.canDrink = canDrink;
        }
        
        return world;
    };
} 